// Conversion pools — MM Web's monitor and rebalance surface (SM-468).
//
// The counterpart of Backoffice's Treasury → Conversion pools (SM-467),
// reading the SAME `/admin/conversions/*` records through the same
// authoritative contract. That is deliberate and it is the phase's first
// compatibility invariant: both consoles must show identical pool
// balances and identical movement results for the same pool. There is no
// MM-specific pool read that could drift.
//
// What is NOT here, on purpose (phase decision log #7): route
// configuration. Enabling a route, or changing the rate, fee or limit a
// user converts at, is a policy action and stays Backoffice-only. MM
// funds and rebalances; it does not decide what conversion costs.
//
// And the distinction that matters most on this screen: **a conversion
// pool is not MM capital.** It is a platform obligation to users holding
// GREEN. It never appears as execution balance, never counts toward a
// risk budget, and no strategy can consume it. Rendering it beside the
// desks on /accounts would invite exactly that mistake, which is why it
// has its own route.
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ROUTE_CRITICAL_KEYS, refreshOnNavigate } from "@/lib/routeRefresh";
import { DataFreshness } from "@/components/ui/DataFreshness";
import { invalidateAffected } from "@/lib/routeRefresh";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { PoolMovementDialog, type PoolMovementKind } from "@/components/pools/PoolMovementDialog";
import {
  currencyKeys,
  fetchConversionPools,
  fetchConversionRoutes,
  fetchPoolMovements,
} from "@/lib/currency-queries";
import type { ConversionPoolStatus } from "@/lib/currency-types";
import { fetchMyPermissions, keys } from "@/lib/queries";
import { conversionPoolsEnabled } from "@/lib/featureFlags";
import { useI18n } from "@/i18n/I18nProvider";
import type { TranslationKey } from "@/i18n/en";

export const Route = createFileRoute("/_app/pools")({
  // SM-563 — re-read this page's data on arrival.
  loader: refreshOnNavigate(ROUTE_CRITICAL_KEYS.pools),
  component: PoolsRoute,
});

const ROLE_SOURCE_POOL = "SOURCE_POOL";
const KIND_FUND: PoolMovementKind = "fund";
const KIND_REBALANCE: PoolMovementKind = "rebalance";
const MOVEMENT_PAGE = 25;

/** Ledger permission required to move pool liquidity. */
const PERM_POOL_MANAGE = "CONVERSION_POOL_MANAGE";

function PoolsRoute() {
  // G1: gates the page's initiation only. A pool movement lives in the
  // ledger, not on this route — nothing in flight is hidden by the flag.
  if (!conversionPoolsEnabled()) return null;
  return <PoolsPage />;
}

function PoolsPage() {
  const { t } = useI18n();
  const qc = useQueryClient();

  const pools = useQuery({ queryKey: currencyKeys.pools(), queryFn: fetchConversionPools });
  const routes = useQuery({ queryKey: currencyKeys.routes(), queryFn: fetchConversionRoutes });
  const perms = useQuery({ queryKey: keys.myPermissions(), queryFn: fetchMyPermissions });

  // While permissions are loading, show the actions: the server enforces
  // them regardless, and a button that appears late reads as a bug.
  const canManage =
    !perms.data ||
    perms.data.roles?.includes("SUPER_ADMIN") ||
    perms.data.permissions?.includes(PERM_POOL_MANAGE);

  const [dialog, setDialog] = useState<{ kind: PoolMovementKind; pool: ConversionPoolStatus } | null>(
    null,
  );

  const sources = (pools.data ?? []).filter((p) => p.role === ROLE_SOURCE_POOL);
  const sinks = (pools.data ?? []).filter((p) => p.role !== ROLE_SOURCE_POOL);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">{t("pools.title")}</h1>
        <p className="mt-1 max-w-[85ch] text-sm text-muted">{t("pools.intro")}</p>
      </header>
      <DataFreshness query={pools} relatedQueries={[routes]} />

      <p className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs text-warning">
        {t("pools.notCapital")}
      </p>

      {pools.isLoading ? (
        <Skeleton className="h-40" />
      ) : pools.isError && !pools.data ? (
        <ErrorBanner error={pools.error} fallbackTitle={t("pools.readFailed")} />
      ) : sources.length === 0 ? (
        <p className="text-sm text-muted">{t("pools.none")}</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {sources.map((pool) => (
            <PoolCard
              key={`${pool.currency}-${pool.role}`}
              pool={pool}
              route={(routes.data ?? []).find((r) => r.destination_currency === pool.currency)}
              canManage={!!canManage}
              onFund={() => setDialog({ kind: KIND_FUND, pool })}
              onRebalance={() => setDialog({ kind: KIND_REBALANCE, pool })}
            />
          ))}
        </div>
      )}

      {sinks.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
            {t("pools.receiptHeading")}
          </h2>
          <div className="grid gap-4 lg:grid-cols-2">
            {sinks.map((pool) => (
              <PoolCard key={`${pool.currency}-${pool.role}`} pool={pool} readOnly />
            ))}
          </div>
        </section>
      )}

      <MovementHistory pools={pools.data ?? []} />

      {dialog && (
        <PoolMovementDialog
          kind={dialog.kind}
          pool={dialog.pool}
          onClose={() => setDialog(null)}
          onDone={() => {
            invalidateAffected(qc, "pools");
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}

function PoolCard({
  pool,
  route,
  canManage,
  readOnly,
  onFund,
  onRebalance,
}: {
  pool: ConversionPoolStatus;
  route?: { enabled: boolean };
  canManage?: boolean;
  readOnly?: boolean;
  onFund?: () => void;
  onRebalance?: () => void;
}) {
  const { t } = useI18n();
  const state = poolState(pool);
  // Utilisation: how much of the pool is currently committed to active
  // conversions. Undefined — not 0% — on an empty pool: 0/0 is not "all
  // free", it is "nothing to be free".
  const utilisation = poolUtilisation(pool);

  return (
    <Card className="space-y-4 p-4">
      <header className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <CurrencyBadge currency={pool.currency} />
          <h3 className="truncate text-sm font-semibold">
            {t("pools.cardTitle", { currency: pool.currency })}
          </h3>
        </div>
        <span className={state.className}>{t(state.labelKey)}</span>
      </header>

      <dl className="grid grid-cols-3 gap-3">
        <Figure label={t("pools.total")} currency={pool.currency} value={pool.total} />
        <Figure label={t("pools.available")} currency={pool.currency} value={pool.available} />
        <Figure label={t("pools.reserved")} currency={pool.currency} value={pool.reserved} />
      </dl>
      <p className="text-xs text-muted">{t("pools.reservedNote")}</p>

      {/* Utilisation, route switch and paying-out are SOURCE-POOL
          facts. The GREEN receipt account has no route and pays nobody
          out, so printing "Route switch: not reported · paying out: no"
          against it would invent a problem it cannot have. */}
      {pool.role === ROLE_SOURCE_POOL && (
        <dl className="space-y-1 border-t border-border pt-3 text-xs">
          <Line
            label={t("pools.utilisation")}
            value={utilisation === undefined ? t("pools.utilisationUnknown") : `${utilisation}%`}
          />
          <Line
            label={t("pools.routeStatus")}
            value={
              route
                ? route.enabled
                  ? t("pools.routeSwitchOn")
                  : t("pools.routeSwitchOff")
                : t("pools.routeUnknown")
            }
          />
          <Line
            label={t("pools.payingOut")}
            value={pool.route_available ? t("common.yes") : t("common.no")}
          />
        </dl>
      )}

      {state.explainKey && (
        <p className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs text-warning">
          {t(state.explainKey)}
        </p>
      )}

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={!canManage} onClick={onFund}>
            {t("pools.fund")}
          </Button>
          <Button variant="ghost" size="sm" disabled={!canManage} onClick={onRebalance}>
            {t("pools.rebalance")}
          </Button>
          {!canManage && <span className="text-xs text-muted">{t("pools.needPermission")}</span>}
          <span className="text-xs text-muted">{t("pools.routeConfigElsewhere")}</span>
        </div>
      )}
    </Card>
  );
}

/**
 * Share of the pool currently committed to active conversions, 0–100.
 *
 * `undefined` when the pool is empty: 0 reserved out of 0 total is not
 * "0% utilised", it is a pool with nothing in it, and rendering a
 * reassuring 0% on an exhausted pool is the reading this guards against.
 */
export function poolUtilisation(pool: ConversionPoolStatus): number | undefined {
  const total = Number(pool.total);
  const reserved = Number(pool.reserved);
  if (!Number.isFinite(total) || !Number.isFinite(reserved) || total <= 0) return undefined;
  return Math.round((reserved / total) * 100);
}

/** Same precedence as Backoffice's: exhausted outranks low outranks unfunded. */
export function poolState(pool: ConversionPoolStatus): {
  labelKey: TranslationKey;
  className: string;
  explainKey?: TranslationKey;
} {
  if (pool.role !== ROLE_SOURCE_POOL) {
    return { labelKey: "pools.stateReceiving", className: "text-xs text-muted" };
  }
  if (pool.exhausted) {
    return {
      labelKey: "pools.stateExhausted",
      className: "text-xs font-semibold text-danger",
      explainKey: "pools.exhaustedExplain",
    };
  }
  if (pool.low) {
    return {
      labelKey: "pools.stateLow",
      className: "text-xs font-semibold text-warning",
      explainKey: "pools.lowExplain",
    };
  }
  if (pool.funded === false) {
    return {
      labelKey: "pools.stateUnfunded",
      className: "text-xs font-semibold text-warning",
      explainKey: "pools.unfundedExplain",
    };
  }
  return { labelKey: "pools.stateFunded", className: "text-xs font-semibold text-success" };
}

function Figure({
  label,
  currency,
  value,
}: {
  label: string;
  currency: string;
  value: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="truncate text-sm">
        <CurrencyAmount currency={currency} value={value} />
      </dd>
    </div>
  );
}

function Line({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right">{value}</dd>
    </div>
  );
}

function MovementHistory({ pools }: { pools: ConversionPoolStatus[] }) {
  const { t } = useI18n();
  const currencies = useMemo(() => [...new Set(pools.map((p) => p.currency))], [pools]);
  const [picked, setPicked] = useState("");
  const active = picked || currencies[0] || "";

  const movements = useQuery({
    queryKey: currencyKeys.poolMovements(active, 0, MOVEMENT_PAGE),
    queryFn: () => fetchPoolMovements(active, { limit: MOVEMENT_PAGE, offset: 0 }),
    enabled: !!active,
  });

  if (!active) return null;

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
          {t("pools.outflowHeading")}
        </h2>
        <div className="flex gap-1">
          {currencies.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={c === active}
              onClick={() => setPicked(c)}
              className={
                c === active
                  ? "rounded-full border border-neon/40 bg-neon/10 px-2 py-0.5 text-xs font-semibold text-neon"
                  : "rounded-full border border-border px-2 py-0.5 text-xs text-muted hover:text-fg"
              }
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <DataFreshness query={movements} />
      {movements.isLoading ? (
        <Skeleton className="h-24" />
      ) : movements.isError && !movements.data ? (
        <ErrorBanner error={movements.error} fallbackTitle={t("pools.movementsFailed")} />
      ) : (movements.data ?? []).length === 0 ? (
        <p className="text-xs text-muted">{t("pools.noMovements", { currency: active })}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[46rem] text-xs">
            <thead>
              <tr className="border-b border-border text-muted">
                <th className="px-3 py-2 text-left">{t("pools.colWhen")}</th>
                <th className="px-3 py-2 text-left">{t("pools.colKind")}</th>
                <th className="px-3 py-2 text-right">{t("pools.colAmount")}</th>
                <th className="px-3 py-2 text-left">{t("pools.colRoute")}</th>
                <th className="px-3 py-2 text-left">{t("pools.colReference")}</th>
              </tr>
            </thead>
            <tbody>
              {(movements.data ?? []).map((m) => (
                <tr key={m.transaction_id} className="border-b border-border last:border-0">
                  <td className="whitespace-nowrap px-3 py-2" title={m.created_at}>
                    {m.created_at.slice(0, 19).replace("T", " ")}
                  </td>
                  <td className="px-3 py-2">{m.kind}</td>
                  <td className="px-3 py-2 text-right">
                    <CurrencyAmount currency={m.currency} value={m.amount} />
                  </td>
                  <td className="px-3 py-2 text-muted">
                    {(m.from_account_code || "—") + " → " + (m.to_account_code || "—")}
                  </td>
                  <td className="px-3 py-2 font-mono" title={m.transaction_id}>
                    {m.operation_id || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-muted">{t("pools.idempotencyNote")}</p>
    </section>
  );
}
